import React, { useState, useEffect, useRef } from "react";
import Footer from "../components/Footer";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
// subscriptionUtils import removed for project-tier checks (flat limit enforced)
import { useTheme } from "@/context/ThemeContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import UserMenu from "@/components/auth/UserMenu";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import Sidebar from "@/components/layout/Sidebar";
import TopBar from "@/components/layout/TopBar";
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  ExternalLink,
  Calendar,
  MapPin,
  Users,
  DollarSign,
  TreePine,
  Leaf,
  Info,
  Droplets,
  Zap,
  Recycle,
  Mountain,
  FlaskConical,
  Heart,
  HelpCircle,
  Home,
  FileText,
  ChevronLeft,
  ChevronRight,
  Bookmark,
  Globe,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ProjectForm } from "@/components/projects/ProjectForm";
import { ProjectDetailModal } from "@/components/projects/ProjectDetailModal";
import InviteGuestModal from "@/components/InviteGuestModalNew";
import { GuestProjectBadge } from "@/components/GuestProjectBadge";
import ManageGuestsModal from "@/components/ManageGuestsModal";
import {
  Project,
  ProjectWithAccess,
  CreateProjectData,
  UpdateProjectData,
  getUserProjects,
  getAllAccessibleProjects,
  createProject,
  updateProject,
  deleteProject,
  getProjectStats,
  searchUserProjects,
  canCreateProject,
} from "@/services/projectService";
import { format, parseISO } from "date-fns";
import { useCurrency } from "@/context/CurrencyContext";

const categoryIcons = {
  Reforestation: TreePine,
  "Wildlife Conservation": Leaf,
  "Water Conservation": Droplets,
  "Renewable Energy": Zap,
  "Waste Reduction": Recycle,
  "Soil Restoration": Mountain,
  "Climate Research": FlaskConical,
  "Community Outreach": Heart,
  Other: HelpCircle,
};

const statusColors = {
  Planned: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  Active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  Completed:
    "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  Paused:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  Cancelled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
};

export default function Projects() {
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();
  const { formatPrice } = useCurrency();
  // No per-tier project limits; flat limit enforced in service
  const { theme } = useTheme();
  const { toast } = useToast();
  const hoverTimer = useRef<NodeJS.Timeout | null>(null);

  // Navigation state
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);

  // State
  const [projects, setProjects] = useState<ProjectWithAccess[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedProjectForInvite, setSelectedProjectForInvite] =
    useState<ProjectWithAccess | null>(null);
  const [showManageGuestsModal, setShowManageGuestsModal] = useState(false);
  const [selectedProjectForGuests, setSelectedProjectForGuests] =
    useState<ProjectWithAccess | null>(null);
  const [projectLimit, setProjectLimit] = useState<{
    allowed: boolean;
    remaining: number;
    message: string;
  } | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    planned: 0,
    paused: 0,
    cancelled: 0,
    totalCollaborators: 0,
    nextDeadline: null as string | null,
  });

  // Load projects on component mount
  useEffect(() => {
    if (user?.id) {
      loadProjects();
      loadStats();
    }
  }, [user?.id]);

  // Filter projects when search or filters change
  useEffect(() => {
    filterProjects();
  }, [projects, searchQuery, statusFilter, categoryFilter]);

  // Navigation functions
  const toggleNavbar = () => {
    setIsNavbarOpen(!isNavbarOpen);
  };

  const handleViewProject = (project: Project) => {
    setSelectedProject(project);
    setShowDetailModal(true);
  };

  const handleEditFromModal = (project: Project) => {
    setEditingProject(project);
    setShowDetailModal(false);
    setShowForm(true);
  };

  const handleDeleteFromModal = (project: Project) => {
    setProjectToDelete(project);
    setShowDetailModal(false);
  };

  const loadProjects = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      console.log("🔄 Loading projects for user:", user.id);
      const projectsData = await getAllAccessibleProjects(user.id);
      console.log("📊 Received projects:", projectsData);
      setProjects(projectsData);

      // Check project limits for all tiers (only for owned projects)
      const limitInfo = await canCreateProject(user);
      setProjectLimit(limitInfo);
    } catch (error) {
      console.error("Error loading projects:", error);
      toast({
        title: "Error",
        description: "Failed to load projects. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!user?.id) return;

    try {
      const statsData = await getProjectStats(user.id);
      setStats(statsData);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const filterProjects = () => {
    let filtered = projects;

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(
        (project) =>
          project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          project.description
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          project.organization
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          project.location?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((project) => project.status === statusFilter);
    }

    // Apply category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter(
        (project) => project.category === categoryFilter,
      );
    }

    setFilteredProjects(filtered);
  };

  const handleCreateProject = async (projectData: CreateProjectData) => {
    if (!user?.id) return;

    // Check project limits before creating
    try {
      const limitCheck = await canCreateProject(user);
      if (!limitCheck.allowed) {
        toast({
          title: "Project Limit Reached",
          description: limitCheck.message,
          variant: "destructive",
        });
        return;
      }
    } catch (error) {
      console.error("Error checking project limits:", error);
    }

    try {
      setIsSubmitting(true);
      const newProject = await createProject(user.id, projectData);
      setProjects([newProject, ...projects]);
      setShowForm(false);
      loadStats(); // Refresh stats

      // Refresh project limits
      const limitInfo = await canCreateProject(user);
      setProjectLimit(limitInfo);

      toast({
        title: "Success",
        description: "Project created successfully!",
      });
    } catch (error) {
      console.error("Error creating project:", error);
      toast({
        title: "Error",
        description: "Failed to create project. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProject = async (projectData: UpdateProjectData) => {
    if (!user?.id) return;

    try {
      setIsSubmitting(true);
      const updatedProject = await updateProject(projectData, user.id);
      setProjects(
        projects.map((p) => (p.id === updatedProject.id ? updatedProject : p)),
      );
      setShowForm(false);
      setEditingProject(null);
      loadStats(); // Refresh stats
      toast({
        title: "Success",
        description: "Project updated successfully!",
      });
    } catch (error) {
      console.error("Error updating project:", error);
      toast({
        title: "Error",
        description: "Failed to update project. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!user?.id) return;

    try {
      await deleteProject(projectId, user.id);
      setProjects(projects.filter((p) => p.id !== projectId));
      setProjectToDelete(null);
      loadStats(); // Refresh stats

      // Refresh project limits
      const limitInfo = await canCreateProject(user);
      setProjectLimit(limitInfo);

      toast({
        title: "Success",
        description: "Project deleted successfully!",
      });
    } catch (error) {
      console.error("Error deleting project:", error);
      toast({
        title: "Error",
        description: "Failed to delete project. Please try again.",
        variant: "destructive",
      });
    }
  };

  const ProjectCard = ({ project }: { project: Project }) => {
    const CategoryIcon = categoryIcons[project.category];

    return (
      <Card
        className="group hover:shadow-lg hover:scale-[1.01] transition-all duration-300 cursor-pointer border border-gray-200 dark:border-blue-700/20 shadow-sm bg-card overflow-hidden"
        onClick={() => handleViewProject(project)}
      >
        {/* Header Strip */}
        <div className="h-2 bg-gradient-to-r from-blue-500 to-emerald-500"></div>

        <CardHeader className="pb-4">
          {/* Top row: Category icon + Title + Actions */}
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <CategoryIcon className="h-6 w-6 text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-card-foreground leading-tight mb-1">
                  {project.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {project.category}
                </p>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 opacity-50 hover:opacity-100 transition-opacity"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewProject(project);
                  }}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View Details
                </DropdownMenuItem>
                {(project.access_type === "owner" ||
                  (project.access_type === "guest" &&
                    project.guest_permissions?.edit)) && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingProject(project);
                      setShowForm(true);
                    }}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                )}
                {project.access_type === "owner" && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProjectForInvite(project);
                      setShowInviteModal(true);
                    }}
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Invite Guest
                  </DropdownMenuItem>
                )}
                {project.access_type === "owner" && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProjectForGuests(project);
                      setShowManageGuestsModal(true);
                    }}
                  >
                    <Users className="mr-2 h-4 w-4" />
                    Manage Guests
                    {(project.guest_count ?? 0) > 0 && (
                      <span className="ml-auto text-xs text-muted-foreground">
                        {project.guest_count}
                      </span>
                    )}
                  </DropdownMenuItem>
                )}
                {project.website_url && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(project.website_url, "_blank");
                    }}
                  >
                    <Globe className="mr-2 h-4 w-4" />
                    Visit Website
                  </DropdownMenuItem>
                )}
                {project.access_type === "owner" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setProjectToDelete(project);
                      }}
                      className="text-red-600 dark:text-red-400"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Badges Row */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <Badge
              className={`${statusColors[project.status]} font-medium text-xs`}
            >
              {project.status}
            </Badge>
            <GuestProjectBadge project={project} />
          </div>

          {/* Guest info if applicable */}
          {project.access_type === "guest" && project.host_email && (
            <div className="flex items-center gap-1 text-sm text-emerald-600 dark:text-emerald-400 mb-3">
              <Users className="h-4 w-4" />
              <span>Shared by {project.host_email}</span>
            </div>
          )}
        </CardHeader>

        <CardContent className="pt-0">
          {/* Description */}
          <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed mb-4">
            {project.description}
          </p>

          {/* Key Info Grid */}
          <div className="grid grid-cols-2 gap-3 text-xs mb-4">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>
                {format(parseISO(project.start_date), "MMM dd, yyyy")}
              </span>
            </div>
            {project.location && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <MapPin className="h-3 w-3" />
                <span className="truncate">{project.location}</span>
              </div>
            )}
            {project.budget && (
              <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                <DollarSign className="h-3 w-3" />
                <span>{formatPrice(project.budget)}</span>
              </div>
            )}
            {project.participants && (
              <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 font-medium">
                <Users className="h-3 w-3" />
                <span>{project.participants} people</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-border/30">
            {project.organization ? (
              <div className="flex items-center gap-2 text-xs">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span className="font-medium text-blue-600 dark:text-blue-400 truncate">
                  {project.organization}
                </span>
              </div>
            ) : (
              <div></div>
            )}

            <div className="text-xs text-muted-foreground">
              {project.access_type === "owner" ? "Owner" : "Collaborator"}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Status pill definitions for the interactive filter bar
  const statusPills: Array<{
    key: string;
    label: string;
    count: number;
    color: string;
    dot: string;
  }> = [
    {
      key: "Active",
      label: "Active",
      count: stats.active,
      color: "text-green-600 dark:text-green-400",
      dot: "bg-green-500",
    },
    {
      key: "Planned",
      label: "Planned",
      count: stats.planned,
      color: "text-blue-600 dark:text-blue-400",
      dot: "bg-blue-500",
    },
    {
      key: "Completed",
      label: "Completed",
      count: stats.completed,
      color: "text-purple-600 dark:text-purple-400",
      dot: "bg-purple-500",
    },
    {
      key: "Paused",
      label: "Paused",
      count: stats.paused,
      color: "text-yellow-600 dark:text-yellow-400",
      dot: "bg-yellow-500",
    },
    {
      key: "Cancelled",
      label: "Cancelled",
      count: stats.cancelled,
      color: "text-red-500 dark:text-red-400",
      dot: "bg-red-500",
    },
  ];

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <p>Please log in to view your projects.</p>
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="container mx-auto px-4 py-6">
        <ProjectForm
          project={editingProject}
          onSubmit={editingProject ? handleUpdateProject : handleCreateProject}
          onCancel={() => {
            setShowForm(false);
            setEditingProject(null);
          }}
          isSubmitting={isSubmitting}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-gradient-to-b from-background to-background/80">
      {/* Collapsible Navbar */}
      <Sidebar />

      <div className="flex-1 flex flex-col">
        {/* Header */}
        <TopBar
          title="Projects"
          icon={<TreePine className="h-4 w-4 text-[#40e0d0]" />}
          showBackButton={false}
          showSubscriptionBadge={true}
        />

        {/* Main Content */}
        <main className="flex-1 container mx-auto px-4 sm:px-6 py-8">
          <div className="space-y-6">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 text-left">
              <div>
                <p className="text-muted-foreground font-semibold mb-5">
                  Manage your environmental conservation projects
                </p>
              </div>

              <div className="flex flex-col items-end mt-4 sm:mt-0">
                {/* If at limit, inform user (upgrade path removed for project allocation) */}
                {projectLimit && projectLimit.remaining === 0 && (
                  <div className="mb-2">
                    <p className="text-xs text-red-500">
                      You have reached the maximum of 10 projects.
                    </p>
                  </div>
                )}

                <Button
                  onClick={() => setShowForm(true)}
                  className=""
                  disabled={projectLimit?.remaining === 0}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New Project
                </Button>

                {/* Project Counter - flat 10-project limit for all users */}
                {projectLimit && (
                  <p
                    className={`text-sm mt-2 text-right ${
                      projectLimit.remaining === 0
                        ? "text-red-500"
                        : projectLimit.remaining <= 1
                          ? "text-amber-500"
                          : "text-green-600"
                    }`}
                  >
                    <span className="font-medium">
                      {projectLimit.remaining}
                    </span>{" "}
                    of 10 projects remaining
                  </p>
                )}
              </div>
            </div>

            {/* Project Statistics */}
            <div className="flex items-center gap-4 bg-[#f3f4f6] dark:bg-[#272727] px-4 py-2 rounded-lg shadow-sm">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground font-medium">
                  {projects.length > 0 && (
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="text-blue-600">
                        {
                          projects.filter((p) => p.access_type === "owner")
                            .length
                        }{" "}
                        Owned
                      </span>
                      {projects.filter((p) => p.access_type === "guest")
                        .length > 0 && (
                        <span className="text-emerald-600">
                          {
                            projects.filter((p) => p.access_type === "guest")
                              .length
                          }{" "}
                          Shared
                        </span>
                      )}
                      <span className="text-muted-foreground">
                        {projects.length} Total
                      </span>
                    </div>
                  )}
                </span>
              </div>

              <div className="ml-auto">
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-sm">
                      Projects you own count toward your project limit. Projects
                      shared with you as a guest do not count toward your limit,
                      but your permissions may be limited by the owner.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            {/* Stats — 2 metric cards + interactive status pill bar */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              {/* Metric card: Active Collaborators */}
              <Card className="flex-none w-full sm:w-48">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                      <Users className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-medium leading-tight">
                        Active Collaborators
                      </p>
                      <p className="text-2xl font-bold leading-tight">
                        {stats.totalCollaborators}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Metric card: Next Deadline */}
              <Card className="flex-none w-full sm:w-48">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-900/20">
                      <Calendar className="h-5 w-5 text-orange-500 dark:text-orange-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground font-medium leading-tight">
                        Next Deadline
                      </p>
                      <p className="text-sm font-bold leading-tight truncate">
                        {stats.nextDeadline
                          ? format(parseISO(stats.nextDeadline), "dd MMM yyyy")
                          : "—"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Status pill bar — each pill is a clickable filter toggle */}
              <Card className="flex-1">
                <CardContent className="p-4 h-full">
                  <div className="flex items-center h-full gap-1 flex-wrap">
                    <button
                      onClick={() => setStatusFilter("all")}
                      className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                        statusFilter === "all"
                          ? "bg-foreground text-background border-foreground"
                          : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                      }`}
                    >
                      All ({stats.total})
                    </button>
                    {statusPills.map((pill) => {
                      const active = statusFilter === pill.key;
                      const hasProjects = pill.count > 0;
                      return (
                        <button
                          key={pill.key}
                          onClick={() =>
                            setStatusFilter(active ? "all" : pill.key)
                          }
                          className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                            active
                              ? "bg-foreground text-background border-foreground"
                              : hasProjects
                                ? `border-border ${pill.color} hover:border-foreground`
                                : "border-border/40 text-muted-foreground/40 cursor-default"
                          }`}
                          disabled={!hasProjects && !active}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              active ? "bg-background" : pill.dot
                            } ${!hasProjects ? "opacity-40" : ""}`}
                          />
                          {pill.label} ({pill.count})
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Planned">Planned</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Paused">Paused</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.keys(categoryIcons).map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Projects Grid */}
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredProjects.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 text-left">
                {filteredProjects.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <TreePine className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  No Projects Found
                </h3>
                <p className="text-muted-foreground mb-4">
                  {projects.length === 0
                    ? "Start your environmental impact journey by creating your first project."
                    : "No projects match your current filters. Try adjusting your search criteria."}
                </p>
                {projects.length === 0 && (
                  <Button onClick={() => setShowForm(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Your First Project
                  </Button>
                )}
              </Card>
            )}

            {/* Delete Confirmation Dialog */}
            <AlertDialog
              open={!!projectToDelete}
              onOpenChange={() => setProjectToDelete(null)}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete
                    the project "{projectToDelete?.name}" and remove all
                    associated data.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() =>
                      projectToDelete && handleDeleteProject(projectToDelete.id)
                    }
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete Project
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Project Detail Modal */}
            <ProjectDetailModal
              project={selectedProject}
              open={showDetailModal}
              onClose={() => {
                setShowDetailModal(false);
                setSelectedProject(null);
              }}
              onEdit={handleEditFromModal}
              onDelete={handleDeleteFromModal}
            />

            {/* Invite Guest Modal */}
            <InviteGuestModal
              isOpen={showInviteModal}
              onClose={() => {
                console.log("Closing invite modal");
                setShowInviteModal(false);
                setSelectedProjectForInvite(null);
              }}
              preselectedProject={selectedProjectForInvite}
            />

            {/* Manage Guests Modal */}
            <ManageGuestsModal
              project={selectedProjectForGuests}
              isOpen={showManageGuestsModal}
              onClose={() => {
                setShowManageGuestsModal(false);
                setSelectedProjectForGuests(null);
              }}
              onGuestRemoved={loadProjects}
            />

            {/* Create/Edit Project Form Modal */}
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
}
